/**
 * Server Profile API — backend calls for the V2 server profile page.
 *
 * READ (apiFetch):
 *   sp_dev_getPalcareDevicesByCampusID   (server basic info)
 *   sp_dev_getareasbycampusid            (area dropdown)
 *   getserverstatushistory               (device status history)
 *   getserverlogs                        (cloud-only logs)
 *   core_config                          (server DB config)
 *   getserverstatusbymac                 (MQTT heartbeat / status)
 *   core_active_event_snapshots          (local alerts)
 *   getmatchterms                        (match term rules)
 *   getvirtualdevices                    (virtual devices)
 *   att_hwi_device_type                  (device type options)
 *   att_event_categories                 (event category options)
 *   att_event_types                      (event type options)
 *
 * WRITE (apiTransaction / apiSave):
 *   server_edit              (basic info update)
 *   core_config POST         (DB config update)
 *   core_matchterm PUT/POST/DELETE (match term CRUD)
 *   core_device PUT/DELETE   (virtual device add/delete)
 *
 * UTILITY (apiValidation / apiMqtt):
 *   ping / validate / fix    (database validation)
 *   getActiveAlert           (MQTT alert interrogation)
 */

import { apiFetch, apiTransaction, apiSave, apiValidation, apiMqtt } from '../../../../services/api.js';

// ---------------------------------------------------------------------------
// READ — Basic server data (600s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch server device info by campus + server ID filter. */
export function fetchServerDevice(campusId, serverId) {
  return apiFetch({
    procedure: 'sp_dev_getPalcareDevicesByCampusID',
    reqType: 'stored',
    payload: { device_filter: serverId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch all areas for the campus (dropdown options). */
export function fetchServerAreas(campusId) {
  return apiFetch({
    procedure: 'sp_dev_getareasbycampusid',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch device status history for a server. */
export function fetchServerStatusHistory(campusId, serverId) {
  return apiFetch({
    procedure: 'getserverstatushistory',
    reqType: 'stored',
    pKey: 'campus_id',
    pVal: campusId,
    payload: { device_filter: serverId, campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch server logs (cloud-only). */
export function fetchServerLogs(serverId) {
  return apiFetch({
    procedure: 'getserverlogs',
    reqType: 'stored',
    pKey: 'palcare_device_id',
    pVal: serverId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// READ — Real-time status (5s polling cycle)
// ---------------------------------------------------------------------------

/** Fetch active event snapshots for a server by MAC address. */
export function fetchLocalAlerts(macAddr) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_active_event_snapshots',
    pKey: 'mac_addr',
    pVal: macAddr.replaceAll(':', ''),
    readOnly: true,
  });
}

/** Fetch server config rows from local DB by MAC address. */
export function fetchServerConfig(macAddr) {
  return apiFetch({
    procedure: 'fetchbyparameter',
    reqType: 'direct',
    table: 'core_config',
    pKey: 'server_id',
    pVal: macAddr.replaceAll(':', ''),
    readOnly: true,
  });
}

/** Fetch MQTT server status (heartbeat, uptime, alarms) by MAC address. */
export function fetchServerMqStatus(macAddr) {
  return apiFetch({
    procedure: 'getserverstatusbymac',
    reqType: 'stored',
    pKey: 'palcare_server_id',
    pVal: macAddr.replaceAll(':', ''),
    payload: { palcare_server_id: macAddr.replaceAll(':', '') },
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// READ — Match terms & virtual devices (600s polling, interfaceId 1 or 2)
// ---------------------------------------------------------------------------

/** Fetch all match terms for a server. */
export function fetchMatchTerms(campusId, serverId) {
  return apiFetch({
    procedure: 'getmatchterms',
    reqType: 'stored',
    pKey: 'palcare_device_id',
    pVal: serverId,
    payload: { campus_id: campusId, palcare_device_id: serverId },
    readOnly: true,
  });
}

/** Fetch all virtual devices for a campus. */
export function fetchVirtualDevices(campusId) {
  return apiFetch({
    procedure: 'getvirtualdevices',
    reqType: 'stored',
    payload: { campus_id: campusId },
    readOnly: true,
  });
}

/** Fetch available virtual device types for a campus. */
export function fetchVirtualDeviceTypes(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_hwi_device_type',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch event categories for a campus. */
export function fetchEventCategories(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_event_categories',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

/** Fetch event types for a campus. */
export function fetchEventTypes(campusId) {
  return apiFetch({
    procedure: 'fetchall',
    reqType: 'direct',
    table: 'att_event_types',
    pKey: 'campus_id',
    pVal: campusId,
    readOnly: true,
  });
}

// ---------------------------------------------------------------------------
// WRITE — Basic info
// ---------------------------------------------------------------------------

/**
 * Update server basic info (name, area, interface, status, local IP).
 *
 * V1 TransType: server_edit
 *
 * @param {object} data — { area_id, description, mac_addr, config_id, palcare_interface_id, interface_change, palcare_device_id, name_change, is_active, ip_address }
 * @param {object} session — { campusId, userId, clientId }
 */
export function editServerBasicInfo(data, session) {
  return apiTransaction({
    transType: 'server_edit',
    payload: [data],
    campusId: session.campusId,
    userId: session.userId,
    clientId: session.clientId,
  });
}

// ---------------------------------------------------------------------------
// WRITE — Database config
// ---------------------------------------------------------------------------

/**
 * Save server database configuration (serial ports, baud rates, env settings).
 *
 * V1: mysave → core_config POST (update by config_id, condition: "primary")
 *
 * @param {Array} configItems — Array of { config_value, config_id, modified_by }
 * @param {object} session — { campusId, userId, clientId }
 */
export function saveServerConfig(configItems, session) {
  return apiSave({
    table: 'core_config',
    payload: configItems,
    pKey: 'config_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'POST',
  });
}

// ---------------------------------------------------------------------------
// WRITE — Match terms
// ---------------------------------------------------------------------------

/**
 * Add one or more match terms.
 *
 * V1: mysave → core_matchterm PUT
 *
 * @param {Array} matchTerms — Array of match term records
 * @param {object} session — { campusId, userId, clientId }
 */
export function addMatchTerms(matchTerms, session) {
  return apiSave({
    table: 'core_matchterm',
    payload: matchTerms,
    pKey: 'matchterm_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'PUT',
  });
}

/**
 * Edit an existing match term.
 *
 * V1: mysave → core_matchterm POST
 *
 * @param {object} matchTerm — Single match term with matchterm_id + updated fields
 * @param {object} session — { campusId, userId, clientId }
 */
export function editMatchTerm(matchTerm, session) {
  return apiSave({
    table: 'core_matchterm',
    payload: [matchTerm],
    pKey: 'matchterm_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'POST',
  });
}

/**
 * Delete match terms (bulk).
 *
 * V1: mysave → core_matchterm DELETE
 *
 * @param {Array} items — Array of { matchterm_id, modified_by }
 * @param {object} session — { campusId, userId, clientId }
 */
export function deleteMatchTerms(items, session) {
  return apiSave({
    table: 'core_matchterm',
    payload: items,
    pKey: 'matchterm_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// WRITE — Virtual devices
// ---------------------------------------------------------------------------

/**
 * Add virtual devices.
 *
 * V1: mysave → core_device PUT
 *
 * @param {Array} devices — Array of virtual device records
 * @param {object} session — { campusId, userId, clientId }
 */
export function addVirtualDevices(devices, session) {
  return apiSave({
    table: 'core_device',
    payload: devices,
    pKey: 'device_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'PUT',
  });
}

/**
 * Delete virtual devices (bulk).
 *
 * V1: mysave → core_device DELETE
 *
 * @param {Array} items — Array of { device_id, modified_by }
 * @param {object} session — { campusId, userId, clientId }
 */
export function deleteVirtualDevices(items, session) {
  return apiSave({
    table: 'core_device',
    payload: items,
    pKey: 'device_id',
    condition: 'primary',
    campusId: session.campusId,
    clientId: session.clientId,
    userId: session.userId,
    method: 'DELETE',
  });
}

// ---------------------------------------------------------------------------
// UTILITY — Database validation
// ---------------------------------------------------------------------------

/** Ping server database to check existence. */
export function pingDatabase(macAddr, campusId) {
  return apiValidation({
    reqType: 'ping',
    payload: { server_id: macAddr.replaceAll(':', ''), campus_id: campusId },
  });
}

/** Validate server database config against expected schema. */
export function validateDatabase(macAddr, campusId, interfaceId) {
  return apiValidation({
    reqType: 'validate',
    payload: { server_id: macAddr.replaceAll(':', ''), campus_id: campusId, interface_id: interfaceId },
  });
}

/** Fix server database by adding missing / removing extra config items. */
export function fixDatabase(macAddr, campusId, interfaceId, missingConfigs, extraConfigs) {
  return apiValidation({
    reqType: 'fix',
    payload: {
      server_id: macAddr.replaceAll(':', ''),
      campus_id: campusId,
      interface_id: interfaceId,
      config_items: missingConfigs,
      config_extras: extraConfigs,
    },
  });
}

// ---------------------------------------------------------------------------
// UTILITY — MQTT interrogation
// ---------------------------------------------------------------------------

/** Interrogate server for active alerts via MQTT. */
export function interrogateAlerts(campusId, clientId, macAddr) {
  return apiMqtt({
    operation: 'getActiveAlert',
    campusId,
    clientId,
    serverId: macAddr.replaceAll(':', ''),
  });
}
